import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styles from "./UserMenu.module.css";
import { Icon } from "./Icon";
import { useCurrentUser } from "../useCurrentUser";
import { authEnabled, signOut } from "../lib/auth";

function initialer(navn: string): string {
  return navn
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();
}

/** Konto som i ACL-portalene: bare avataren vises, navn, e-post, lenke til
 *  Innstillinger (tema, stil, farge) og utlogging ligger i menyen. «up» åpner oppover (nederst i sidemenyen),
 *  «down» nedover (topplinjen på mobil). */
export function UserMenu({ placement = "up" }: { placement?: "up" | "down" }) {
  const user = useCurrentUser();
  const [apen, setApen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!apen) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setApen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setApen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [apen]);

  if (!user) return <span className={styles.avatar} aria-hidden="true" />;

  const avatar = (stor?: boolean) => (
    <span className={`${styles.avatar} ${stor ? styles.avatarLg : ""}`}>
      {user.avatarUrl ? <img src={user.avatarUrl} alt="" /> : initialer(user.displayName)}
    </span>
  );

  return (
    <div ref={ref} className={styles.wrap}>
      <button
        type="button"
        className={`${styles.avatarBtn} ${apen ? styles.on : ""}`}
        onClick={() => setApen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={apen}
        title={`${user.displayName} · ${user.email}`}
        aria-label={`Konto: ${user.displayName}`}
      >
        {avatar()}
      </button>
      {apen && (
        <div className={`${styles.popover} ${placement === "down" ? styles.down : styles.up}`} role="menu">
          <div className={styles.head}>
            {avatar(true)}
            <div className={styles.who}>
              <div className={styles.name}>{user.displayName}</div>
              <div className={styles.muted}>{user.email}</div>
              {user.role && <span className={styles.rolle}>{user.role}</span>}
            </div>
          </div>
          <div className={styles.sep} />
          <Link className={styles.item} to="/profil" onClick={() => setApen(false)}>
            <Icon name="user" size={16} /> Min profil
          </Link>
          <Link className={styles.item} to="/innstillinger" onClick={() => setApen(false)}>
            <Icon name="settings" size={16} /> Innstillinger
          </Link>
          {authEnabled && (
            <>
              <div className={styles.sep} />
              <button type="button" className={styles.item} onClick={signOut}>
                <Icon name="logout" size={16} /> Logg ut
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
